import React from "react";
import styled, { keyframes } from "styled-components";
import { skills } from "../../data/constants";

// Rotation animation
const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const reverseRotate = keyframes`
  from {
    transform: rotate(360deg);
  }
  to {
    transform: rotate(0deg);
  }
`;

const OrbitContainer = styled.div`
  position: relative;
  width: 420px;
  height: 420px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    width: 320px;
    height: 320px;
  }

  @media (max-width: 480px) {
    width: 280px;
    height: 280px;
  }
`;

const OuterOrbitPath = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  border: 2px dashed rgba(133, 76, 230, 0.3);
  border-radius: 50%;
`;

const InnerOrbitPath = styled.div`
  position: absolute;
  width: 75%;
  height: 75%;
  border: 2px dashed rgba(133, 76, 230, 0.3);
  border-radius: 50%;
`;

const OuterOrbitWrapper = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  animation: ${rotate} 25s linear infinite;
`;

const InnerOrbitWrapper = styled.div`
  position: absolute;
  width: 75%;
  height: 75%;
  animation: ${reverseRotate} 18s linear infinite;
`;

const Tooltip = styled.div`
  position: absolute;
  top: -35px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(133, 76, 230, 0.95);
  color: white;
  padding: 5px 10px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  z-index: 1000;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 5px solid transparent;
    border-top-color: rgba(133, 76, 230, 0.95);
  }
`;

const OuterTechIcon = styled.div`
  position: absolute;
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: ${({ theme }) => theme.card || '#171721'};
  border: 2px solid rgba(133, 76, 230, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 0 15px rgba(133, 76, 230, 0.2);
  animation: ${reverseRotate} 25s linear infinite;

  img {
    width: 28px;
    height: 28px;
    object-fit: contain;
  }

  &:hover {
    border-color: #854CE6;
    box-shadow: 0 0 25px rgba(133, 76, 230, 0.6);
    z-index: 100;
  }
  
  &:hover + ${Tooltip} {
    opacity: 1;
    visibility: visible;
  }

  @media (max-width: 768px) {
    width: 38px;
    height: 38px;

    img {
      width: 22px;
      height: 22px;
    }
  }

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;

    img {
      width: 18px;
      height: 18px;
    }
  }
`;

const InnerTechIcon = styled.div`
  position: absolute;
  width: 45px;
  height: 45px;
  border-radius: 50%;
  background: ${({ theme }) => theme.card || '#171721'};
  border: 2px solid rgba(133, 76, 230, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.3s ease, border-color 0.3s ease, box-shadow 0.3s ease;
  box-shadow: 0 0 15px rgba(133, 76, 230, 0.2);
  animation: ${rotate} 18s linear infinite;

  img {
    width: 28px;
    height: 28px;
    object-fit: contain;
  }

  &:hover {
    border-color: #854CE6;
    box-shadow: 0 0 25px rgba(133, 76, 230, 0.6);
    z-index: 100;
  }
  
  &:hover + ${Tooltip} {
    opacity: 1;
    visibility: visible;
  }

  @media (max-width: 768px) {
    width: 38px;
    height: 38px;

    img {
      width: 22px;
      height: 22px;
    }
  }

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;

    img {
      width: 18px;
      height: 18px;
    }
  }
`;

const IconWrapper = styled.div`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ProfileImage = styled.img`
  width: 55%;
  height: 55%;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid ${({ theme }) => theme.primary || '#854CE6'};
  box-shadow: 0 0 30px rgba(133, 76, 230, 0.4);
  z-index: 10;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 0 50px rgba(133, 76, 230, 0.6);
    transform: scale(1.02);
  }
`;

const TechOrbit = ({ profileImage }) => {
  // Get all skills from all categories
  const allSkills = skills.flatMap(category => category.skills);
  
  // Select icons for outer orbit (first 8)
  const outerOrbitSkills = allSkills.slice(0, 8);
  // Select icons for inner orbit (next 6)
  const innerOrbitSkills = allSkills.slice(8, 14);

  // Calculate position on orbit
  const getPosition = (index, total, radius) => {
    const angle = (index / total) * 2 * Math.PI - Math.PI / 2;
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    return { x, y };
  };

  return (
    <OrbitContainer>
      {/* Orbit paths */}
      <OuterOrbitPath />
      <InnerOrbitPath />

      {/* Outer orbit icons */}
      <OuterOrbitWrapper>
        {outerOrbitSkills.map((skill, index) => {
          const pos = getPosition(index, outerOrbitSkills.length, 50);
          return (
            <IconWrapper
              key={skill.name}
              style={{
                top: `${50 + pos.y}%`,
                left: `${50 + pos.x}%`,
                transform: 'translate(-50%, -50%)',
              }}
            >
              <OuterTechIcon>
                <img src={skill.image} alt={skill.name} />
              </OuterTechIcon>
              <Tooltip>{skill.name}</Tooltip>
            </IconWrapper>
          );
        })}
      </OuterOrbitWrapper>

      {/* Inner orbit icons */}
      <InnerOrbitWrapper>
        {innerOrbitSkills.map((skill, index) => {
          const pos = getPosition(index, innerOrbitSkills.length, 50);
          return (
            <IconWrapper
              key={skill.name}
              style={{
                top: `${50 + pos.y}%`,
                left: `${50 + pos.x}%`,
                transform: 'translate(-50%, -50%)',
              }}
            >
              <InnerTechIcon>
                <img src={skill.image} alt={skill.name} />
              </InnerTechIcon>
              <Tooltip>{skill.name}</Tooltip>
            </IconWrapper>
          );
        })}
      </InnerOrbitWrapper>

      {/* Profile image in center */}
      <ProfileImage src={profileImage} alt="Profile" />
    </OrbitContainer>
  );
};

export default TechOrbit;
